import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { authService } from '../features/user/userService';
import { getUserCart } from '../features/user/userSlice';
import { base_url } from '../utils/axiosConfig';
import axios from 'axios';
import { FaArrowLeft, FaBox, FaFileDownload, FaCheckCircle, FaTruck, FaTimesCircle, FaUndo, FaShoppingCart, FaMapMarkerAlt, FaCreditCard, FaClipboardList } from 'react-icons/fa';
import { MdCancel, MdRepeat } from 'react-icons/md';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import logoImg from '../images/shaka-removebg-preview.png';

/* ─── Status config ─────────────────────────────────────── */
const STATUS_CONFIG = {
  Pending: { color: '#f59e0b', bg: '#fef3c7', icon: <FaBox /> },
  Processing: { color: '#3b82f6', bg: '#dbeafe', icon: <FaBox /> },
  Shipped: { color: '#8b5cf6', bg: '#ede9fe', icon: <FaTruck /> },
  Delivered: { color: '#10b981', bg: '#d1fae5', icon: <FaCheckCircle /> },
  Cancelled: { color: '#ef4444', bg: '#fee2e2', icon: <FaTimesCircle /> },
  Return: { color: '#f97316', bg: '#ffedd5', icon: <FaUndo /> },
};

/* ─── TrackOrder delivery stages ────────────────────────── */
const TRACK_STAGES = ['Ordered', 'In Transit', 'Out for Delivery', 'Delivered'];
const TRACK_STAGE_ICONS = {
  'Ordered': '🏪',
  'In Transit': '🚚',
  'Out for Delivery': '🛵',
  'Delivered': '✅',
};
const getTrackStepIndex = (status) => {
  const idx = TRACK_STAGES.indexOf(status);
  return idx === -1 ? 0 : idx;
};
const calcTrackProgress = (status) =>
  ((getTrackStepIndex(status) + 1) / TRACK_STAGES.length) * 100;

const OrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.auth);

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [repeating, setRepeating] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [trackedOrder, setTrackedOrder] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await authService.getOrderById(id);
        setOrder(response.order);
        // Auto-fetch live tracking using the order's tracking number
        if (response.order?.trackingNumber) {
          try {
            const token = JSON.parse(localStorage.getItem('customer'))?.token;
            const trackRes = await axios.post(
              `${base_url}user/trackorder`,
              { trackingNumber: response.order.trackingNumber },
              { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
            );
            if (trackRes.data) setTrackedOrder(trackRes.data);
          } catch {
            // tracking fetch is best-effort, silently ignore
          }
        }
      } catch {
        toast.error('Failed to fetch order details');
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id]);

  const handleRepeatOrder = async () => {
    setRepeating(true);
    let outOfStockItems = [];
    try {
      if (isAuthenticated) {
        for (const item of order.orderItems) {
          let variantStr = item.variant;
          let unitTitle = item.unitTitle;
          let matchedVar = null;

          if (item.product?.variants?.length > 0) {
             matchedVar = item.product.variants.find(v => 
               (variantStr && v.measurementUnit === variantStr) || 
               ((v.offerPrice || v.CurrentPrice) === item.price)
             ) || item.product.variants[0];
             
             if (!variantStr) {
               variantStr = matchedVar?.measurementUnit;
               unitTitle = matchedVar?.unit?.title;
             }
          }

          let finalQuantity = item.quantity;
          if (matchedVar && typeof matchedVar.quantity !== 'undefined') {
             finalQuantity = Math.min(item.quantity, matchedVar.quantity);
          }

          if (finalQuantity <= 0) {
            outOfStockItems.push(item.product?.title);
            continue;
          }

          await authService.addToCart({
            ProductId: item.product._id,
            quantity: finalQuantity,
            price: item.price,
            variant: variantStr || '',
            unitTitle: unitTitle || '',
          });
        }
        await dispatch(getUserCart());
      } else {
        let localCart = JSON.parse(localStorage.getItem('cart')) || [];
        order.orderItems.forEach((item) => {
          let variantStr = item.variant;
          let unitTitle = item.unitTitle;
          let matchedVar = null;

          if (item.product?.variants?.length > 0) {
             matchedVar = item.product.variants.find(v => 
               (variantStr && v.measurementUnit === variantStr) || 
               ((v.offerPrice || v.CurrentPrice) === item.price)
             ) || item.product.variants[0];
             
             if (!variantStr) {
               variantStr = matchedVar?.measurementUnit;
               unitTitle = matchedVar?.unit?.title;
             }
          }

          let finalQuantity = item.quantity;
          if (matchedVar && typeof matchedVar.quantity !== 'undefined') {
             finalQuantity = Math.min(item.quantity, matchedVar.quantity);
          }

          if (finalQuantity <= 0) {
            outOfStockItems.push(item.product?.title);
            return;
          }

          const index = localCart.findIndex((i) => i.ProductId._id === item.product._id && i.variant === variantStr);
          if (index >= 0) {
            localCart[index].quantity += finalQuantity;
          } else {
            localCart.push({ 
              ProductId: item.product, 
              price: item.price, 
              quantity: finalQuantity,
              variant: variantStr || '',
              unitTitle: unitTitle || ''
            });
          }
        });
        localStorage.setItem('cart', JSON.stringify(localCart));
      }

      if (outOfStockItems.length > 0) {
         toast.warning(`Some items were skipped because they are out of stock: ${outOfStockItems.join(', ')}`, { autoClose: 5000 });
      } else {
         toast.success('All items added to cart!');
      }
      
      navigate('/cart');
    } catch {
      toast.error('Failed to add items to cart');
    } finally {
      setRepeating(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!cancelReason.trim()) return toast.error('Provide a cancellation reason');
    setCancelling(true);
    try {
      const token = JSON.parse(localStorage.getItem('customer'))?.token;
      if (!token) throw new Error('Authentication required');
      const res = await axios.put(
        `${base_url}user/cancelorder/${id}`,
        { reason: cancelReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        setOrder({ ...order, orderStatus: 'Cancelled', cancellationReason: cancelReason });
        toast.success('Order cancelled');
        setCancelDialogOpen(false);
        setCancelReason('');
      } else toast.error(res.data.message);
    } catch (err) {
      toast.error(err.message || 'Cancel failed');
    } finally {
      setCancelling(false);
    }
  };

  /* ── Loading ── */
  if (loading) {
    return (
      <div style={styles.loaderWrap}>
        <div style={styles.spinner} />
        <p style={{ color: '#777', marginTop: 12, fontFamily: 'Rubik, sans-serif' }}>Fetching order details…</p>
      </div>
    );
  }

  /* ── Not found ── */
  if (!order) {
    return (
      <div style={styles.loaderWrap}>
        <FaBox size={48} color="#ccc" />
        <p style={{ color: '#555', marginTop: 12 }}>Order not found.</p>
        <button style={styles.primaryBtn} onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  const statusCfg = STATUS_CONFIG[order.orderStatus] || STATUS_CONFIG.Pending;
  const isCancelledOrReturn = order.orderStatus === 'Cancelled' || order.orderStatus === 'Return';
  // Use live tracking status if available, else fall back to order status for stepper
  const trackStatus = trackedOrder?.orderStatus || order.orderStatus;

  const calculateTotals = () => {
    if (!order?.orderItems) return { subtotal: 0, totalGst: 0 };
    return order.orderItems.reduce((acc, item) => {
      const itemSub = (item.price || 0) * (item.quantity || 0);
      const gstPercent = parseFloat(item.product?.gst) || 0;
      const gstAmt = (itemSub * gstPercent) / 100;
      return {
        subtotal: acc.subtotal + itemSub,
        totalGst: acc.totalGst + gstAmt
      };
    }, { subtotal: 0, totalGst: 0 });
  };

  const { subtotal, totalGst } = calculateTotals();

  const generateInvoice = async () => {
    if (!order) return;
    
    const toastId = toast.loading("Generating High-Quality Invoice...");
    
    try {
      const element = document.getElementById('invoice-download-template');
      // Make visible briefly off-screen for rendering
      element.style.display = 'block';
      element.style.position = 'absolute';
      element.style.left = '-9999px';
      
      const canvas = await html2canvas(element, { 
        scale: 2, 
        useCORS: true, 
        logging: false 
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`Shakambari_Invoice_${order.trackingNumber}.pdf`);
      
      // Hide again
      element.style.display = 'none';
      toast.update(toastId, { render: "Invoice Downloaded Successfully!", type: "success", isLoading: false, autoClose: 3000 });
    } catch (error) {
      console.error(error);
      toast.update(toastId, { render: "Failed to generate invoice", type: "error", isLoading: false, autoClose: 3000 });
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* ── Back button ── */}
        <button style={styles.backBtn} onClick={() => navigate(-1)}>
          <FaArrowLeft size={14} style={{ marginRight: 6 }} /> Back to Orders
        </button>

        {/* ── Page title ── */}
        <div style={styles.pageHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h1 style={styles.pageTitle}>Order Details</h1>
            <span style={{ ...styles.statusBadge, backgroundColor: statusCfg.bg, color: statusCfg.color }}>
              {statusCfg.icon}&nbsp;&nbsp;{order.orderStatus}
            </span>
          </div>
          
          <button style={styles.invoiceBtn} onClick={generateInvoice}>
            <FaFileDownload size={14} style={{ marginRight: 6 }} /> 
            Download Invoice
          </button>
        </div>

        {/* ── Layout grid ── */}
        <div style={styles.grid}>

          {/* ─── LEFT column ─────────────────────────────── */}
          <div style={styles.leftCol}>

            {/* Order Summary card */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <FaClipboardList style={styles.cardHeaderIcon} />
                <span>Order Summary</span>
              </div>
              <div style={styles.cardBody}>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Order ID</span>
                  <span style={styles.trackingNumber}>{order.orderId || order.trackingNumber}</span>
                </div>
                <div style={styles.divider} />
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Order Date</span>
                  <span style={styles.infoValue}>{new Date(order.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Order Time</span>
                  <span style={styles.infoValue}>{new Date(order.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Customer Email</span>
                  <span style={styles.infoValue}>{order.user?.email}</span>
                </div>
                {order.cancellationReason && (
                  <div style={styles.alertBox('#fee2e2', '#b91c1c')}>
                    <strong>Cancellation Reason:</strong> {order.cancellationReason}
                  </div>
                )}
                {order.orderStatus === 'Return' && order.returnReason && (
                  <div style={styles.alertBox('#ffedd5', '#c2410c')}>
                    <strong>Return Reason:</strong> {order.returnReason}
                  </div>
                )}
              </div>
            </div>

            {/* ── Order Progress stepper (from TrackOrder) ── */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <FaTruck style={styles.cardHeaderIcon} />
                <span>Order Progress</span>
                {trackedOrder && (
                  <span style={styles.liveChip}>● Live</span>
                )}
              </div>
              <div style={{ padding: '18px 18px 14px' }}>
                {isCancelledOrReturn ? (
                  <div style={styles.cancelledBox}>
                    <FaTimesCircle color="#ef4444" size={20} />
                    <div>
                      <p style={{ fontWeight: 700, color: '#ef4444', margin: 0, fontSize: 14 }}>
                        Order {order.orderStatus}
                      </p>
                      {order.cancellationReason && (
                        <p style={{ fontSize: 12, color: '#b91c1c', margin: '3px 0 0' }}>Reason: {order.cancellationReason}</p>
                      )}
                      {order.returnReason && (
                        <p style={{ fontSize: 12, color: '#c2410c', margin: '3px 0 0' }}>Reason: {order.returnReason}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div style={styles.stepperWrap}>
                    {/* Background track */}
                    <div style={styles.stepperBgLine} />
                    {/* Orange progress fill */}
                    <div style={{ ...styles.stepperFill, width: `${calcTrackProgress(trackStatus)}%` }} />
                    {/* Steps */}
                    {TRACK_STAGES.map((stage, idx) => {
                      const completed = idx <= getTrackStepIndex(trackStatus);
                      const active = idx === getTrackStepIndex(trackStatus);
                      return (
                        <div key={stage} style={styles.stepItem}>
                          <div style={{
                            ...styles.stepCircle,
                            backgroundColor: completed ? (active ? '#ff6701' : '#10b981') : '#e5e7eb',
                            boxShadow: active ? '0 0 0 5px rgba(255,103,1,0.18)' : 'none',
                            transform: active ? 'scale(1.18)' : 'scale(1)',
                          }}>
                            <span style={{ fontSize: 18 }}>{TRACK_STAGE_ICONS[stage]}</span>
                          </div>
                          <p style={{
                            ...styles.stepLabel,
                            color: completed ? '#232f3e' : '#aaa',
                            fontWeight: active ? 700 : 500,
                          }}>{stage}</p>
                          {active && <p style={styles.stepCurrent}>Current</p>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Payment & Delivery card */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <FaCreditCard style={styles.cardHeaderIcon} />
                <span>Payment & Delivery</span>
              </div>
              <div style={styles.cardBody}>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Payment Method</span>
                  <span style={{ ...styles.infoValue, ...styles.paymentChip }}>
                    {order.paymentInfo?.method === 'COD' ? '💵 Cash on Delivery' : (order.paymentInfo?.method || '💳 Online Payment')}
                    {order.paymentInfo?.paymentStatus && ` (${order.paymentInfo.paymentStatus})`}
                  </span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Delivery Instructions</span>
                  <span style={styles.infoValue}>{order.deliveryInstruction || 'None'}</span>
                </div>
              </div>
            </div>

            {/* Shipping address card */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <FaMapMarkerAlt style={styles.cardHeaderIcon} />
                <span>Shipping Address</span>
              </div>
              <div style={styles.cardBody}>
                <p style={styles.addressLine}>{order.shippingInfo?.address}</p>
                {order.shippingInfo?.landmark && (
                  <p style={{ ...styles.addressLine, color: '#777' }}>Near: {order.shippingInfo.landmark}</p>
                )}
                <p style={styles.addressLine}>
                  {order.shippingInfo?.city}, {order.shippingInfo?.state}
                </p>
                <p style={{ ...styles.addressLine, fontWeight: 600 }}>PIN: {order.shippingInfo?.pincode}</p>
                <p style={{ ...styles.addressLine, marginTop: '8px', color: '#444' }}>
                  📞 <strong>{order.phoneNumber || order.user?.phoneNumber || order.user?.mobile || "N/A"}</strong>
                </p>
              </div>
            </div>
          </div>

          {/* ─── RIGHT column ────────────────────────────── */}
          <div style={styles.rightCol}>

            {/* Order Items card */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <FaShoppingCart style={styles.cardHeaderIcon} />
                <span>Items ({order.orderItems?.length})</span>
              </div>
              <div style={{ padding: '0 4px' }}>
                {order.orderItems?.map((item, idx) => {
                  const variant = item.product?.variants?.find(v => (v.offerPrice || v.CurrentPrice) === item.price);
                  return (
                    <div key={item._id} style={{ ...styles.productRow, borderBottom: idx < order.orderItems.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                      <div style={styles.productImgWrap}>
                        <img
                          src={item.product?.images?.[0]?.url ? `${base_url}${item.product.images[0].url}` : '/default.jpg'}
                          alt={item.product?.title || 'Product'}
                          style={styles.productImg}
                        />
                      </div>
                      <div style={styles.productInfo}>
                        <p style={styles.productTitle}>{item.product?.title || 'Product'}</p>
                        <div style={styles.productMetaContainer}>
                          {variant && (
                            <div style={styles.variantBadge}>{variant.measurementUnit} {variant.unit?.title}</div>
                          )}
                          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                            <span style={styles.productMeta}>Qty: <strong>{item.quantity}</strong></span>
                            <span style={styles.productMeta}>Unit Price: <strong>₹{item.price}</strong></span>
                            {item.product?.gst && (
                              <span style={styles.productMeta}>GST: <strong>{item.product.gst}%</strong></span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div style={styles.productPrice}>
                        {item.quantity} × ₹{item.price} = <span style={{ color: '#ff6701' }}>₹{(item.price * item.quantity)?.toFixed(2) || 0}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Price breakdown */}
              <div style={styles.priceBox}>
                <div style={styles.priceRow}>
                  <span style={{ color: '#777' }}>Item Subtotal</span>
                  <span>₹{subtotal.toFixed(2)}</span>
                </div>
                <div style={styles.priceRow}>
                  <span style={{ color: '#777' }}>GST Amount</span>
                  <span>₹{totalGst.toFixed(2)}</span>
                </div>
                <div style={styles.priceRow}>
                  <span style={{ color: '#777' }}>Delivery</span>
                  <span style={{ color: '#10b981' }}>FREE</span>
                </div>
                <div style={{ ...styles.divider, margin: '10px 0' }} />
                <div style={{ ...styles.priceRow, fontWeight: 700, fontSize: 18 }}>
                  <span>Total Payable</span>
                  <span style={{ color: '#ff6701' }}>₹{order.totalPrice?.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div style={styles.actionsCard}>
              <button
                style={{ ...styles.primaryBtn, opacity: (repeating || order.orderStatus === 'Cancelled') ? 0.6 : 1 }}
                onClick={handleRepeatOrder}
                disabled={repeating || order.orderStatus === 'Cancelled'}
              >
                <MdRepeat size={16} style={{ marginRight: 6 }} />
                {repeating ? 'Adding to cart…' : 'Repeat Order'}
              </button>

              {!isCancelledOrReturn && (
                <button
                  style={styles.dangerBtn}
                  onClick={() => setCancelDialogOpen(true)}
                >
                  <MdCancel size={16} style={{ marginRight: 6 }} />
                  Cancel Order
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Cancel Dialog Overlay ── */}
      {cancelDialogOpen && (
        <div style={styles.overlay} onClick={() => !cancelling && setCancelDialogOpen(false)}>
          <div style={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <div style={styles.dialogHeader}>
              <MdCancel size={22} color="#ef4444" style={{ marginRight: 8 }} />
              <h2 style={styles.dialogTitle}>Cancel Order</h2>
            </div>
            <p style={styles.dialogSubtitle}>Please tell us why you want to cancel this order.</p>
            <label style={styles.inputLabel}>Reason for Cancellation</label>
            <textarea
              style={styles.textarea}
              placeholder="e.g. Found a better price, changed my mind…"
              rows={4}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              disabled={cancelling}
              autoFocus
            />
            <div style={styles.dialogActions}>
              <button
                style={styles.ghostBtn}
                onClick={() => setCancelDialogOpen(false)}
                disabled={cancelling}
              >
                Keep Order
              </button>
              <button
                style={{ ...styles.dangerBtn, opacity: (!cancelReason.trim() || cancelling) ? 0.55 : 1 }}
                onClick={handleCancelOrder}
                disabled={!cancelReason.trim() || cancelling}
              >
                {cancelling ? 'Cancelling…' : 'Confirm Cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Hidden Invoice HTML Template for perfect Tamil rendering & HTML2Canvas PDF conversion ── */}
      <div id="invoice-download-template" style={{ display: 'none', width: '210mm', padding: '15mm', backgroundColor: '#fff', color: '#000', fontFamily: 'sans-serif', boxSizing: 'border-box' }}>
         
         <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '3px solid #ff6701', paddingBottom: '15px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
               <img src={logoImg} alt="Logo" style={{ width: '65px', height: '65px', objectFit: 'contain' }} />
               <div>
                 <h1 style={{ margin: 0, color: '#ff6701', fontSize: '28px', fontWeight: '900', letterSpacing: '-0.5px' }}>Shakambari</h1>
                 <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '13px', fontWeight: '500' }}>Premium Natural Products</p>
               </div>
            </div>
            <div style={{ textAlign: 'right', marginTop: '10px' }}>
               <h2 style={{ margin: 0, fontSize: '24px', color: '#111', fontWeight: '800' }}>TAX INVOICE</h2>
               <p style={{ margin: '8px 0 2px 0', fontSize: '12px', color: '#444' }}>Order ID: <strong>{order?.orderId || order?.trackingNumber}</strong></p>
               <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#444' }}>Date: <strong>{new Date(order?.createdAt).toLocaleDateString('en-IN')}</strong></p>
            </div>
         </div>
         
         <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '25px' }}>
            <div style={{ width: '45%', padding: '12px', backgroundColor: '#f9f9f9', borderRadius: '8px', border: '1px solid #eee' }}>
               <h3 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#888', textTransform: 'uppercase' }}>Sold By</h3>
               <p style={{ margin: '2px 0', fontSize: '14px', fontWeight: 'bold', color: '#111' }}>Shakambari E-Commerce</p>
               <p style={{ margin: '2px 0', fontSize: '12px', color: '#444' }}>123 Market Street</p>
               <p style={{ margin: '2px 0', fontSize: '12px', color: '#444' }}>Tamil Nadu, India</p>
               <p style={{ margin: '6px 0 0 0', fontSize: '11px', color: '#666' }}>GSTIN: <strong>33ABCDE1234F1Z5</strong></p>
            </div>
            <div style={{ width: '45%', padding: '12px', backgroundColor: '#f9f9f9', borderRadius: '8px', border: '1px solid #eee' }}>
               <h3 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#888', textTransform: 'uppercase' }}>Billing / Shipping Address</h3>
               <p style={{ margin: '2px 0', fontSize: '14px', fontWeight: 'bold', color: '#111' }}>{order?.user?.firstname ? `${order.user.firstname} ${order.user.lastname || ''}` : "Customer"}</p>
               <p style={{ margin: '2px 0', fontSize: '12px', color: '#444' }}>{order?.shippingInfo?.address}</p>
               <p style={{ margin: '2px 0', fontSize: '12px', color: '#444' }}>{order?.shippingInfo?.city}, {order?.shippingInfo?.state} - {order?.shippingInfo?.pincode}</p>
               {order?.shippingInfo?.landmark && <p style={{ margin: '2px 0', fontSize: '12px', color: '#777' }}>Near: {order.shippingInfo.landmark}</p>}
               <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#444' }}>Mobile: <strong>{order?.phoneNumber || order?.user?.phoneNumber || order?.user?.mobile || order?.shippingInfo?.mobile || "N/A"}</strong></p>
               <p style={{ margin: '2px 0', fontSize: '12px', color: '#444' }}>Email: <strong>{order?.user?.email || "Guest"}</strong></p>
            </div>
         </div>
         
         <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px', border: '1px solid #ddd' }}>
            <thead>
               <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th style={{ padding: '12px 10px', textAlign: 'center', fontSize: '12px', borderBottom: '2px solid #ddd', width: '5%' }}>S.No</th>
                  <th style={{ padding: '12px 10px', textAlign: 'left', fontSize: '12px', borderBottom: '2px solid #ddd', width: '40%' }}>Product Description</th>
                  <th style={{ padding: '12px 10px', textAlign: 'center', fontSize: '12px', borderBottom: '2px solid #ddd', width: '10%' }}>Qty</th>
                  <th style={{ padding: '12px 10px', textAlign: 'right', fontSize: '12px', borderBottom: '2px solid #ddd', width: '15%' }}>Gross</th>
                  <th style={{ padding: '12px 10px', textAlign: 'right', fontSize: '12px', borderBottom: '2px solid #ddd', width: '10%' }}>GST</th>
                  <th style={{ padding: '12px 10px', textAlign: 'right', fontSize: '12px', borderBottom: '2px solid #ddd', width: '20%' }}>Total</th>
               </tr>
            </thead>
            <tbody>
               {order?.orderItems?.map((item, index) => {
                 const price = item.price || 0;
                 const gstPercent = parseFloat(item.product?.gst) || 0;
                 const gstAmt = (price * item.quantity * gstPercent) / 100;
                 const total = (price * item.quantity) + gstAmt;
                 
                 // Advanced variant extraction (API mapping fallback)
                 let variantStr = item.variant;
                 let unitTitle = item.unitTitle;
                 if (!variantStr && item.product?.variants?.length > 0) {
                    const matchedVar = item.product.variants.find(v => (v.offerPrice || v.CurrentPrice) === price) || item.product.variants[0];
                    variantStr = matchedVar?.measurementUnit;
                    unitTitle = matchedVar?.unit?.title;
                 }
                 const finalVariantText = variantStr ? `${variantStr} ${unitTitle || ""}` : "";
                 
                 return (
                   <tr key={index}>
                      <td style={{ padding: '12px 10px', fontSize: '12px', textAlign: 'center', borderBottom: '1px solid #eee', color: '#555' }}>{index + 1}</td>
                      <td style={{ padding: '12px 10px', fontSize: '13px', borderBottom: '1px solid #eee' }}>
                         <div style={{ fontWeight: 'bold', color: '#111' }}>{item.product?.title}</div>
                         {finalVariantText && <div style={{ fontSize: '11px', color: '#777', marginTop: '4px' }}>Variant: {finalVariantText}</div>}
                      </td>
                      <td style={{ padding: '12px 10px', textAlign: 'center', fontSize: '12px', borderBottom: '1px solid #eee', fontWeight: 'bold' }}>{item.quantity}</td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: '12px', borderBottom: '1px solid #eee', color: '#444' }}>₹{price.toFixed(2)}</td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: '12px', borderBottom: '1px solid #eee', color: '#444' }}>{gstPercent}%</td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: '13px', borderBottom: '1px solid #eee', fontWeight: 'bold', color: '#111' }}>₹{total.toFixed(2)}</td>
                   </tr>
                 );
               })}
            </tbody>
         </table>
         
         <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '40px' }}>
            <div style={{ width: '45%' }}>
               <h4 style={{ margin: '0 0 5px 0', fontSize: '12px', color: '#888' }}>Payment Method</h4>
               <p style={{ margin: 0, fontSize: '13px', fontWeight: 'bold' }}>{order?.paymentInfo?.method || order?.paymentIntent?.method || "N/A"} <span style={{ color: '#10b981', fontWeight: 'normal', fontSize: '11px', marginLeft: '5px' }}>({order?.paymentInfo?.paymentStatus || order?.paymentIntent?.status || "N/A"})</span></p>
            </div>
            
            <div style={{ width: '40%', padding: '15px', backgroundColor: '#fcfcfc', border: '1px solid #eee', borderRadius: '8px' }}>
               <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px', color: '#444' }}>
                  <span>Subtotal</span>
                  <span>₹{subtotal.toFixed(2)}</span>
               </div>
               <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px', color: '#444' }}>
                  <span>Total GST</span>
                  <span>₹{totalGst.toFixed(2)}</span>
               </div>
               <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 0 0', marginTop: '10px', borderTop: '1px dashed #ccc', fontSize: '16px', fontWeight: '900', color: '#ff6701' }}>
                  <span>Grand Total</span>
                  <span>₹{(subtotal + totalGst).toFixed(2)}</span>
               </div>
            </div>
         </div>
         
         <div style={{ textAlign: 'center', color: '#999', fontSize: '11px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
            Thank you for shopping with Shakambari!<br/>
            This is a computer-generated invoice. No physical signature is required.
         </div>
      </div>

    </div>
  );
};

/* ─────────── Inline styles ─────────────────────────────── */
const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f5f5f7',
    fontFamily: '"Rubik", "Poppins", sans-serif',
    paddingBottom: 60,
  },
  container: {
    maxWidth: 1100,
    margin: '0 auto',
    padding: '24px 16px',
  },
  loaderWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '70vh',
    fontFamily: '"Rubik", sans-serif',
  },
  spinner: {
    width: 44,
    height: 44,
    border: '4px solid #f0f0f0',
    borderTop: '4px solid #ff6701',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  backBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    background: 'none',
    border: '1.5px solid #e0e0e0',
    borderRadius: 8,
    padding: '7px 16px',
    fontSize: 13,
    fontWeight: 500,
    color: '#444',
    cursor: 'pointer',
    marginBottom: 20,
    fontFamily: 'inherit',
    transition: 'all 0.2s',
  },
  pageHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
    flexWrap: 'wrap',
    gap: 12,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: '#1c1c1b',
    margin: 0,
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 16px',
    borderRadius: 20,
    fontWeight: 600,
    fontSize: 13,
  },
  invoiceBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: '#ff6701',
    color: '#fff',
    border: 'none',
    padding: '8px 18px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(255,103,1,0.25)',
    transition: 'all 0.2s ease',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: 20,
    alignItems: 'start',
  },
  leftCol: { display: 'flex', flexDirection: 'column', gap: 16 },
  rightCol: { display: 'flex', flexDirection: 'column', gap: 16 },
  card: {
    background: '#fff',
    borderRadius: 14,
    boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
    overflow: 'hidden',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '14px 18px',
    borderBottom: '1px solid #f0f0f0',
    fontWeight: 700,
    fontSize: 14,
    color: '#232f3e',
    backgroundColor: '#fafafa',
  },
  cardHeaderIcon: { color: '#ff6701', fontSize: 16 },
  cardBody: { padding: '14px 18px' },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    flexWrap: 'wrap',
    gap: 4,
  },
  infoLabel: { fontSize: 13, color: '#888', fontWeight: 500 },
  infoValue: { fontSize: 13, color: '#333', fontWeight: 600, textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word' },
  trackingNumber: {
    fontSize: 13,
    fontWeight: 700,
    color: '#ff6701',
    letterSpacing: 0.5,
    fontFamily: 'monospace',
  },
  divider: { height: 1, backgroundColor: '#f0f0f0', margin: '4px 0' },
  alertBox: (bg, text) => ({
    marginTop: 12,
    padding: '10px 14px',
    borderRadius: 8,
    backgroundColor: bg,
    color: text,
    fontSize: 13,
    lineHeight: 1.5,
    borderLeft: `3px solid ${text}`,
  }),
  paymentChip: {
    backgroundColor: '#f0fdf4',
    color: '#166534',
    padding: '3px 10px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 700,
  },
  addressLine: { fontSize: 13, color: '#444', marginBottom: 4 },
  /* ─ Live chip ─ */
  liveChip: {
    marginLeft: 'auto', fontSize: 11, fontWeight: 700,
    color: '#10b981', backgroundColor: '#d1fae5',
    padding: '2px 8px', borderRadius: 20,
  },
  /* ─ Cancelled box ─ */
  cancelledBox: {
    display: 'flex', alignItems: 'flex-start', gap: 12,
    padding: '12px 16px', borderRadius: 10,
    backgroundColor: '#fef2f2', border: '1px solid #fecaca',
  },
  /* ─ Horizontal stepper ─ */
  stepperWrap: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    position: 'relative', padding: '16px 8px 8px',
    backgroundColor: '#f9fafb', borderRadius: 12, overflowX: 'auto',
  },
  stepperBgLine: {
    position: 'absolute', top: 36, left: 44, right: 44,
    height: 4, backgroundColor: '#e5e7eb', zIndex: 1,
  },
  stepperFill: {
    position: 'absolute', top: 36, left: 44,
    height: 4, backgroundColor: '#ff6701', zIndex: 2,
    transition: 'width 0.5s ease',
    maxWidth: 'calc(100% - 88px)',
  },
  stepItem: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    zIndex: 3, minWidth: 70, gap: 4,
  },
  stepCircle: {
    width: 48, height: 48, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all 0.3s ease', marginBottom: 2,
    backgroundColor: '#e5e7eb',
  },
  stepLabel: {
    fontSize: 11, textAlign: 'center', margin: 0,
    lineHeight: 1.3, maxWidth: 70,
  },
  stepCurrent: {
    fontSize: 10, color: '#ff6701', fontWeight: 700, margin: 0,
  },
  /* Product rows */
  productRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '14px 18px',
  },
  productImgWrap: {
    width: 64, height: 64, flexShrink: 0,
    borderRadius: 10, overflow: 'hidden',
    backgroundColor: '#f5f5f7',
    border: '1px solid #eee',
  },
  productImg: { width: '100%', height: '100%', objectFit: 'cover' },
  productInfo: { flex: 1, minWidth: 0 },
  productTitle: { fontSize: 14, fontWeight: 600, color: '#1c1c1b', marginBottom: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  productMetaContainer: { display: 'flex', flexDirection: 'column', gap: 6 },
  variantBadge: {
    display: 'inline-block',
    backgroundColor: '#fff7ed',
    color: '#c2410c',
    border: '1px solid #fed7aa',
    padding: '2px 8px',
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 700,
    width: 'fit-content'
  },
  productMeta: { fontSize: 12, color: '#666', margin: 0 },
  productPrice: { fontSize: 15, fontWeight: 700, color: '#232f3e', whiteSpace: 'nowrap' },
  /* Price box */
  priceBox: { padding: '14px 18px', borderTop: '1px solid #f0f0f0', backgroundColor: '#fafafa' },
  priceRow: { display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 500, marginBottom: 6 },
  /* Actions */
  actionsCard: {
    background: '#fff',
    borderRadius: 14,
    padding: 18,
    boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  primaryBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: '100%', padding: '12px 20px',
    backgroundColor: '#232f3e', color: '#fff',
    border: 'none', borderRadius: 10,
    fontWeight: 600, fontSize: 14, cursor: 'pointer',
    fontFamily: 'inherit', transition: 'background 0.2s',
  },
  dangerBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: '100%', padding: '12px 20px',
    backgroundColor: '#fff', color: '#ef4444',
    border: '1.5px solid #ef4444', borderRadius: 10,
    fontWeight: 600, fontSize: 14, cursor: 'pointer',
    fontFamily: 'inherit', transition: 'all 0.2s',
  },
  ghostBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    padding: '11px 20px', flex: 1,
    backgroundColor: '#f5f5f7', color: '#555',
    border: '1.5px solid #e0e0e0', borderRadius: 10,
    fontWeight: 600, fontSize: 14, cursor: 'pointer',
    fontFamily: 'inherit',
  },
  /* Dialog */
  overlay: {
    position: 'fixed', inset: 0,
    backgroundColor: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999, padding: 16,
  },
  dialog: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: '28px 24px',
    width: '100%', maxWidth: 460,
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
    fontFamily: '"Rubik", sans-serif',
  },
  dialogHeader: { display: 'flex', alignItems: 'center', marginBottom: 8 },
  dialogTitle: { fontSize: 18, fontWeight: 700, color: '#1c1c1b', margin: 0 },
  dialogSubtitle: { fontSize: 13, color: '#888', marginBottom: 20 },
  inputLabel: { display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 8 },
  textarea: {
    width: '100%', padding: '12px 14px',
    border: '1.5px solid #e0e0e0', borderRadius: 10,
    fontSize: 13, fontFamily: 'inherit', color: '#333',
    resize: 'vertical', outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  },
  dialogActions: { display: 'flex', gap: 10, marginTop: 18 },
};

export default OrderDetails;